/**
 * Venta rápida: el selector múltiple de productos de un cliente.
 *
 * Es lo que pidió el dueño, textual: «cada cliente que agregues, que haya una
 * sección donde diga producto, ahí seleccionas y te salen todos los productos
 * que vendemos con su precio y su vencimiento, y que puedas seleccionar más de
 * uno, y eso mismo te hace la cuenta de lo que debe el cliente».
 *
 * UN componente para los DOS lados
 * --------------------------------
 * El mismo diálogo lo abre la ficha del cliente (`VentaRapidaPanel`) y el acceso
 * rápido del inicio (`VentaRapidaLauncher`). No hay dos selectores: hay uno con
 * dos puertas. Lo único que cambia entre un lado y el otro es quién le pasa el
 * `partnerId` —la ficha ya lo sabe, el inicio lo pregunta primero—.
 *
 * De dónde salen los datos
 * ------------------------
 * De DOS acciones de servidor, y de nada más:
 *
 *  1. `base.partner.catalogo_venta_rapida` (sólo lectura) devuelve todo lo
 *     vendible con **el precio ya resuelto para ESE cliente** —su lista de
 *     precios y sus descuentos ya aplicados— y con cada cuánto vence cada cosa.
 *     Acá no se calcula ningún precio: mostrar un número calculado del lado del
 *     cliente sería prometer un importe que la factura no va a repetir.
 *  2. `base.partner.venta_rapida` registra lo marcado y devuelve los dos totales
 *     del cliente ya recalculados.
 *  3. `base.partner.consorcios_del_cliente` (sólo lectura, y de **property_mgmt**
 *     y no de `sales`) devuelve los consorcios a los que se le puede anotar un
 *     trámite de este cliente. Es lo que llena el selector «¿Para qué
 *     consorcio?» de cada renglón. Si no está —un despliegue sin el vertical—
 *     la lista sale vacía, el selector no se dibuja y el diálogo funciona
 *     exactamente como antes.
 *
 * El consorcio se elige POR RENGLÓN
 * ---------------------------------
 * Porque una venta junta varios trámites del mismo administrador y cada uno
 * puede ser de un edificio distinto: obleas de Rivadavia, RSU de Belgrano y la
 * DDJJ del propio administrador, todo en el mismo acto. Con un selector arriba
 * habría que partir eso en tres ventas. Cuando algún renglón trae consorcio, el
 * confirmar llama a `venta_rapida_por_consorcio` —la envolvente del vertical,
 * que **usa** la de `sales` y le agrega el vínculo—; si no trae ninguno, llama a
 * la de siempre.
 *
 * Lo que este diálogo NO hace
 * ---------------------------
 * **No factura nada, ni siquiera en borrador.** Confirmar deja anotado qué se le
 * vendió, por cuánto y cuándo vence; el comprobante es un botón aparte, después,
 * decidido por una persona (ARQUITECTURA.md §9). El cartel del pie lo dice con
 * todas las letras, porque quien aprieta «Confirmar» tiene que saber qué acaba
 * de pasar y qué no.
 *
 * La plata no pasa por `number`
 * -----------------------------
 * Las cantidades y los precios se guardan como TEXTO en el estado y viajan como
 * texto: un `number` de JavaScript no puede representar 12,30 y en un precio eso
 * son centavos que no cierran. El total se suma con enteros de centavos por lo
 * mismo. Es la misma regla que el backend aplica del otro lado.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { controlClass } from '@/components/fields/controls';
import { formatCurrency } from '@/components/fields/format';
import { parsearNumero } from '@/components/fields/parse-numero';
import {
  AvisoDeCatalogoTruncado,
  FichaDelProducto,
  datosDe,
  deCentavos,
  filtrarCatalogo,
  subtotalEnCentavos,
  useCatalogoDeVenta,
} from '@/components/venta/catalogo';
import type { CatalogoDeVenta, ProductoOfrecido } from '@/components/venta/catalogo';
import { IconAlert, IconCheck, IconSearch, IconSpinner } from '@/components/shell/icons';
import { Button, EmptyState, LoadingState } from '@/components/shell/primitives';
import { notify } from '@/components/shell/toast';
import { useEstadoDeVentaRapida, useSalidaAEmpresaQueVende } from '@/views/EmpresaQueVende';
import { AvisoDeBloqueo, BotonBloqueado } from '@/views/MotivoBloqueo';
import { ApiError, api } from '@/lib/api';

const MODELO = 'base.partner';

// El catálogo, sus tipos y la ficha de un producto viven en
// `components/venta/catalogo`: los comparte con el widget «Qué se le vendió» del
// formulario de Ventas. Se reexportan acá para no romper a quien los importaba
// desde este módulo.
export type { CatalogoDeVenta, ProductoOfrecido };

/** Los dos totales (`quick_sale.TotalesDelCliente`). Nunca se suman. */
export interface TotalesDelCliente {
  partner_name: string;
  invoiced_unpaid: string;
  invoiced_overdue: string;
  invoiced_count: number;
  committed_unbilled: string;
  committed_count: number;
  committed_in_draft: string;
}

/** Lo que devuelve `base.partner.consorcios_del_cliente`. */
interface CatalogoDeConsorcios {
  partner_id: number;
  consorcios: ConsorcioOfrecido[];
  count: number;
}

/** Lo que devuelve confirmar (`quick_sale.ResultadoVentaRapida`). */
interface ResultadoVentaRapida {
  created: number;
  total: string;
  invoices_created: number;
  totals: TotalesDelCliente;
}

/** Lo que el usuario marcó de un producto: tildado, cuánto, a qué precio y para quién. */
interface Marcado {
  /** Cantidad tal como se tipeó. Texto, nunca `number`. */
  cantidad: string;
  /** Precio pisado a mano. Vacío = el que resolvió el servidor. */
  precio: string;
  /**
   * Para qué consorcio es ESTE trámite. Vacío = ninguno.
   *
   * Es un dato **por renglón** y no de la venta entera, y no es un capricho: el
   * dueño dijo que una venta junta varios trámites del mismo administrador, y
   * cada uno puede ser de un edificio distinto —las obleas de Rivadavia, el RSU
   * de Belgrano y la DDJJ del propio administrador en el mismo acto—. Con un
   * solo selector arriba habría que hacer tres ventas o volver a corregir dos.
   *
   * Vacío es un caso normal y no un olvido: la matrícula y las DDJJ son del
   * administrador y no son de ningún consorcio. Por eso el control **no es
   * obligatorio** y por eso su opción por defecto está rotulada.
   *
   * Viaja como texto, igual que la cantidad y el precio: es el `value` de un
   * `<select>`.
   */
  consorcio: string;
}

/** Un consorcio ofrecible, tal como lo devuelve `consorcios_del_cliente`. */
interface ConsorcioOfrecido {
  id: number;
  name: string;
  number: string;
}

// ---------------------------------------------------------------------------
// Los dos totales, dibujados como dos cosas distintas
// ---------------------------------------------------------------------------
export interface TotalesProps {
  totales: TotalesDelCliente | null;
  /** Con `compacto` se dibujan en una fila, para el pie del diálogo. */
  compacto?: boolean;
}

/**
 * Los dos números de «lo que debe el cliente», **separados y rotulados**.
 *
 * No hay un tercer número que los sume, y no es un olvido: sumarlos haría que la
 * administradora le reclamara a un cliente plata que todavía no le facturó. Uno
 * es deuda exigible (tiene comprobante confirmado); el otro es un compromiso
 * (todavía no). Por eso van en dos cajas con dos rótulos y dos colores, y no en
 * dos renglones de la misma tabla.
 */
export function TotalesDelClientePanel({ totales, compacto = false }: TotalesProps) {
  if (totales === null) return null;

  return (
    <div className={compacto ? 'grid gap-2 sm:grid-cols-2' : 'grid gap-3 sm:grid-cols-2'}>
      <div className="rounded-lg border border-line bg-app-subtle p-3">
        <p className="text-2xs font-semibold uppercase tracking-wide text-fg-faint">
          Facturado y sin pagar
        </p>
        <p className="mt-1 text-lg font-semibold tabular-nums text-danger">
          {formatCurrency(totales.invoiced_unpaid, { minimumFractionDigits: 2 })}
        </p>
        <p className="mt-0.5 text-xs text-fg-muted">
          Deuda exigible en {totales.invoiced_count} comprobante(s).
          {Number(totales.invoiced_overdue) !== 0 && (
            <>
              {' '}
              <span className="font-medium text-danger">
                {formatCurrency(totales.invoiced_overdue, { minimumFractionDigits: 2 })} vencido.
              </span>
            </>
          )}
        </p>
      </div>

      <div className="rounded-lg border border-line bg-app-subtle p-3">
        <p className="text-2xs font-semibold uppercase tracking-wide text-fg-faint">
          Vendido y sin facturar
        </p>
        <p className="mt-1 text-lg font-semibold tabular-nums text-warning">
          {formatCurrency(totales.committed_unbilled, { minimumFractionDigits: 2 })}
        </p>
        <p className="mt-0.5 text-xs text-fg-muted">
          Comprometido en {totales.committed_count} producto(s): todavía no es exigible.
          {Number(totales.committed_in_draft) !== 0 && (
            <>
              {' '}
              {formatCurrency(totales.committed_in_draft, { minimumFractionDigits: 2 })} ya tiene
              factura en borrador esperando que la confirmen.
            </>
          )}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// El diálogo
// ---------------------------------------------------------------------------
export interface VentaRapidaDialogProps {
  /** A quién se le vende. `null` no dibuja nada. */
  partnerId: number | null;
  /** Nombre para el subtítulo mientras carga el catálogo. */
  partnerName?: string;
  onClose: () => void;
  /** Se llama después de confirmar, para que la pantalla de atrás se refresque. */
  onDone?: (resultado: ResultadoVentaRapida) => void;
}

export function VentaRapidaDialog({
  partnerId,
  partnerName = '',
  onClose,
  onDone,
}: VentaRapidaDialogProps) {
  const panel = useRef<HTMLDivElement>(null);
  const focoPrevio = useRef<HTMLElement | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [marcados, setMarcados] = useState<Record<number, Marcado>>({});
  const [hecho, setHecho] = useState<ResultadoVentaRapida | null>(null);
  /**
   * Por qué el servidor no anotó la venta.
   *
   * Se dibuja EN EL PIE del diálogo, pegado al botón que la persona apretó. El
   * aviso flotante que había antes sale en la esquina, por detrás de un modal
   * de 3xl de ancho: el gesto se sentía como «apreté "Anotar la venta" y no
   * pasó nada».
   */
  const [errorDelServidor, setErrorDelServidor] = useState<string | null>(null);
  const abierto = partnerId !== null;

  // La empresa activa del selector de arriba, ¿puede facturar esta venta? Es la
  // última pantalla antes de escribir, y la que más importa: acá la persona ya
  // marcó los productos y ve el total. El motivo lo escribe el servidor y el
  // freno de verdad está en el servicio —éste no reemplaza a aquél—: si alguien
  // llega igual, la API contesta 400 con el MISMO texto.
  const venta = useEstadoDeVentaRapida();
  const salidaDeEmpresa = useSalidaAEmpresaQueVende();
  const empresaNoVende = venta.existe && !venta.habilitada;

  const catalogo = useCatalogoDeVenta(abierto ? partnerId : null);

  /**
   * Los consorcios a los que se le puede anotar un trámite de este cliente.
   *
   * Sale de una acción de **property_mgmt** (`consorcios_del_cliente`) y no de
   * `sales`: el módulo de ventas no puede saber qué es un consorcio
   * (ARQUITECTURA.md §8), así que quien contesta es el vertical.
   *
   * Si la acción no existe —un despliegue sin el vertical— o falla, la consulta
   * queda en `null` y **el selector no se dibuja**: la venta rápida sigue
   * funcionando exactamente como antes. La integración es opcional en las dos
   * puntas, no sólo en el backend.
   */
  const consorcios = useQuery({
    queryKey: ['consorcios-del-cliente', partnerId] as const,
    enabled: abierto,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: async () => {
      try {
        const respuesta = await api.callAction(
          MODELO,
          partnerId as number,
          'consorcios_del_cliente',
          {},
        );
        return datosDe<CatalogoDeConsorcios>(respuesta)?.consorcios ?? [];
      } catch {
        return [];
      }
    },
  });
  const ofrecidos = consorcios.data ?? [];

  // Al cerrar y volver a abrir, el selector arranca limpio: dejar tildado lo de
  // la venta anterior es como se vende dos veces lo mismo sin querer.
  useEffect(() => {
    if (!abierto) return;
    setBusqueda('');
    setMarcados({});
    setHecho(null);
  }, [abierto, partnerId]);

  useEffect(() => {
    if (!abierto) return;
    focoPrevio.current = document.activeElement as HTMLElement | null;
    const alTeclear = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        evento.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', alTeclear, true);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const temporizador = window.setTimeout(() => {
      panel.current?.querySelector<HTMLElement>('#venta-rapida-buscar')?.focus();
    }, 0);
    return () => {
      document.removeEventListener('keydown', alTeclear, true);
      document.body.style.overflow = overflow;
      window.clearTimeout(temporizador);
      focoPrevio.current?.focus();
    };
  }, [abierto, onClose]);

  const productos = catalogo.data?.products ?? [];

  const visibles = useMemo(() => filtrarCatalogo(productos, busqueda), [productos, busqueda]);

  /** Total de lo marcado, en centavos exactos. Se recalcula mientras marca. */
  const totalCentavos = useMemo(() => {
    let suma = 0;
    for (const producto of productos) {
      const marca = marcados[producto.product_id];
      if (marca === undefined) continue;
      const precio = marca.precio.trim() === '' ? producto.price : marca.precio;
      const subtotal = subtotalEnCentavos(marca.cantidad, precio);
      if (subtotal !== null) suma += subtotal;
    }
    return suma;
  }, [productos, marcados]);

  const cuantosMarcados = Object.keys(marcados).length;

  const confirmar = useMutation({
    mutationFn: async () => {
      const renglones = productos
        .filter((p) => marcados[p.product_id] !== undefined)
        .map((p) => {
          const marca = marcados[p.product_id] as Marcado;
          const cantidad = parsearNumero(marca.cantidad.trim());
          const precio = marca.precio.trim();
          return {
            product_id: p.product_id,
            quantity: cantidad === '' ? '1' : cantidad,
            // Sólo viaja el precio si lo pisaron: si va vacío, lo resuelve el
            // servidor con la lista del cliente, que es la fuente correcta.
            ...(precio === '' ? {} : { unit_price: parsearNumero(precio) }),
            // Y sólo viaja el consorcio si lo eligieron. Un `consorcio_id`
            // vacío y la ausencia de la clave significan lo mismo —«este
            // trámite no es de ningún edificio»— y mandar la clave vacía
            // obligaría al servidor a distinguir dos formas del mismo dato.
            ...(marca.consorcio === '' ? {} : { consorcio_id: Number(marca.consorcio) }),
          };
        });
      // DOS acciones, una sola pantalla. Si alguien eligió consorcio se llama a
      // la de `property_mgmt`, que además de anotar la venta escribe para qué
      // edificio es cada renglón; si no, a la de siempre. No es una duplicación
      // escondida: la envolvente **llama** a la de `sales` y no la reimplementa.
      // Se decide acá y no en el servidor porque el servidor de ventas no puede
      // conocer al vertical, que es la restricción de arquitectura entera.
      const conConsorcio = renglones.some((r) => 'consorcio_id' in r);
      const respuesta = await api.callAction(
        MODELO,
        partnerId as number,
        conConsorcio ? 'venta_rapida_por_consorcio' : 'venta_rapida',
        { renglones: JSON.stringify(renglones) },
      );
      return datosDe<ResultadoVentaRapida>(respuesta);
    },
    onSuccess: (resultado) => {
      if (resultado === null) {
        notify.error('No se pudo leer la respuesta', 'La venta puede haber quedado registrada.');
        return;
      }
      setHecho(resultado);
      setErrorDelServidor(null);
      notify.success(
        'Venta anotada',
        `${resultado.created} producto(s) por ${formatCurrency(resultado.total, { minimumFractionDigits: 2 })}. No se emitió ninguna factura.`,
      );
      onDone?.(resultado);
    },
    onError: (error) => {
      // El motivo va al pie del diálogo, al lado del botón que se apretó. Un
      // aviso flotante detrás del modal no lo lee nadie.
      setErrorDelServidor(
        error instanceof ApiError
          ? error.message
          : 'No se pudo llegar al servidor. Fijate la conexión y probá otra vez.',
      );
    },
  });

  if (!abierto) return null;

  const tildar = (producto: ProductoOfrecido, tildado: boolean): void => {
    setMarcados((actual) => {
      const copia = { ...actual };
      if (tildado) copia[producto.product_id] = { cantidad: '1', precio: '', consorcio: '' };
      else delete copia[producto.product_id];
      return copia;
    });
  };

  const editar = (id: number, campo: keyof Marcado, valor: string): void => {
    setMarcados((actual) => {
      const marca = actual[id];
      if (marca === undefined) return actual;
      return { ...actual, [id]: { ...marca, [campo]: valor } };
    });
  };

  const titulo = catalogo.data?.partner_name ?? partnerName;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-2 sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-fg/30 animate-fade-in" onClick={onClose} aria-hidden="true" />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="venta-rapida-titulo"
        className="relative flex max-h-[92vh] w-full max-w-3xl flex-col rounded-xl border border-line bg-surface shadow-lg animate-slide-up"
      >
        {/* Cabecera */}
        <div className="shrink-0 border-b border-line p-4 sm:p-5">
          <h2 id="venta-rapida-titulo" className="text-lg font-semibold">
            Venta rápida
          </h2>
          <p className="mt-1 text-sm text-fg-muted">
            {titulo === '' ? 'Marcá lo que le vendiste.' : `A ${titulo}.`}{' '}
            {catalogo.data?.pricelist_name !== undefined && catalogo.data.pricelist_name !== '' && (
              <>Precios de la lista «{catalogo.data.pricelist_name}».</>
            )}
          </p>
        </div>

        {hecho !== null ? (
          <ResumenDeLaVenta resultado={hecho} onClose={onClose} onOtra={() => { setHecho(null); setMarcados({}); }} />
        ) : (
          <>
            {/* Antes que el catálogo: si la empresa activa no vende, se dice
                acá arriba y no recién en el botón del pie. Los precios se
                siguen mostrando —mirar no rompe nada y el catálogo es el mismo
                en todas las empresas—, pero nadie llega al final creyendo que
                la venta va a quedar anotada. */}
            {empresaNoVende && (
              <div className="shrink-0 border-b border-line p-4 sm:px-5 sm:py-3">
                <AvisoDeBloqueo tono="atencion" motivo={venta.motivo} salida={salidaDeEmpresa} />
              </div>
            )}

            {/* Buscador */}
            <div className="shrink-0 border-b border-line p-4 sm:px-5 sm:py-3">
              <label htmlFor="venta-rapida-buscar" className="sr-only">
                Buscar un producto
              </label>
              <div className="relative">
                <IconSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-faint" />
                <input
                  id="venta-rapida-buscar"
                  type="search"
                  value={busqueda}
                  onChange={(evento) => {
                    setBusqueda(evento.target.value);
                  }}
                  placeholder="Buscar por nombre, código o rubro…"
                  className={controlClass(false, 'pl-9')}
                />
              </div>
            </div>

            {/* La lista con casillas */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              {catalogo.isPending ? (
                <LoadingState label="Buscando los precios de este cliente…" />
              ) : catalogo.isError ? (
                <div className="flex items-start gap-3 p-5">
                  <IconAlert className="h-6 w-6 shrink-0 text-danger" />
                  <div>
                    <p className="font-semibold">No se pudo abrir el catálogo</p>
                    <p className="mt-1 text-sm text-fg-muted">
                      {catalogo.error instanceof ApiError
                        ? catalogo.error.message
                        : 'Reintentá en un momento.'}
                    </p>
                  </div>
                </div>
              ) : visibles.length === 0 ? (
                <EmptyState
                  title={productos.length === 0 ? 'No hay productos para vender' : 'Ningún producto coincide'}
                  description={
                    productos.length === 0
                      ? 'Cargá productos marcados como «se puede vender» en Ventas ▸ Productos y van a aparecer acá con su precio y su vencimiento.'
                      : 'Probá con otra parte del nombre, el código o el rubro.'
                  }
                />
              ) : (
                <ul className="divide-y divide-line">
                  {visibles.map((producto) => {
                    const marca = marcados[producto.product_id];
                    const tildado = marca !== undefined;
                    const precioEfectivo =
                      marca !== undefined && marca.precio.trim() !== '' ? marca.precio : producto.price;
                    const subtotal =
                      marca === undefined ? null : subtotalEnCentavos(marca.cantidad, precioEfectivo);
                    return (
                      <li
                        key={producto.product_id}
                        className={tildado ? 'bg-accent-soft/30 px-4 py-3 sm:px-5' : 'px-4 py-3 sm:px-5'}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id={`producto-${producto.product_id}`}
                            checked={tildado}
                            onChange={(evento) => {
                              tildar(producto, evento.target.checked);
                            }}
                            className="mt-1 h-4 w-4 shrink-0 rounded border-line text-accent"
                          />
                          <div className="min-w-0 flex-1">
                            <FichaDelProducto
                              producto={producto}
                              htmlFor={`producto-${producto.product_id}`}
                            />

                            {/* Cantidad y precio a mano: sólo de lo tildado. */}
                            {tildado && (
                              <div className="mt-2 flex flex-wrap items-end gap-3">
                                <div className="w-24">
                                  <label
                                    htmlFor={`cantidad-${producto.product_id}`}
                                    className="block text-2xs font-medium uppercase tracking-wide text-fg-faint"
                                  >
                                    Cantidad
                                  </label>
                                  <input
                                    id={`cantidad-${producto.product_id}`}
                                    inputMode="decimal"
                                    value={marca.cantidad}
                                    onChange={(evento) => {
                                      editar(producto.product_id, 'cantidad', evento.target.value);
                                    }}
                                    className={controlClass(false)}
                                  />
                                </div>
                                <div className="w-36">
                                  <label
                                    htmlFor={`precio-${producto.product_id}`}
                                    className="block text-2xs font-medium uppercase tracking-wide text-fg-faint"
                                  >
                                    Precio a mano
                                  </label>
                                  <input
                                    id={`precio-${producto.product_id}`}
                                    inputMode="decimal"
                                    placeholder={producto.price}
                                    value={marca.precio}
                                    onChange={(evento) => {
                                      editar(producto.product_id, 'precio', evento.target.value);
                                    }}
                                    className={controlClass(false)}
                                  />
                                </div>
                                {/* «¿Para qué consorcio?», el tercer control del
                                    renglón. Entra al lado de los otros dos
                                    porque el contenedor ya es un `flex-wrap`:
                                    en pantalla angosta baja solo. Se dibuja
                                    sólo si este cliente TIENE consorcios —a un
                                    proveedor o a un particular no se le
                                    pregunta algo que no aplica— y la opción por
                                    defecto está rotulada: «Ninguno» a secas se
                                    leería como un dato faltante, y no lo es. */}
                                {ofrecidos.length > 0 && (
                                  <div className="w-52">
                                    <label
                                      htmlFor={`consorcio-${producto.product_id}`}
                                      className="block text-2xs font-medium uppercase tracking-wide text-fg-faint"
                                    >
                                      ¿Para qué consorcio?
                                    </label>
                                    <select
                                      id={`consorcio-${producto.product_id}`}
                                      value={marca.consorcio}
                                      onChange={(evento) => {
                                        editar(producto.product_id, 'consorcio', evento.target.value);
                                      }}
                                      className={controlClass(false)}
                                    >
                                      <option value="">Trámite del administrador</option>
                                      {ofrecidos.map((consorcio) => (
                                        <option key={consorcio.id} value={String(consorcio.id)}>
                                          {consorcio.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                                {subtotal !== null && (
                                  <p className="pb-2 text-sm">
                                    <span className="text-fg-muted">Subtotal: </span>
                                    <span className="font-semibold tabular-nums">
                                      {formatCurrency(deCentavos(subtotal), { minimumFractionDigits: 2 })}
                                    </span>
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              {catalogo.data?.truncated === true && <AvisoDeCatalogoTruncado />}
            </div>

            {/* Pie: el total y la advertencia que no se negocia */}
            <div className="shrink-0 border-t border-line p-4 sm:p-5">
              {/* Por qué no se anotó. Arriba del botón que se apretó, fijo, y
                  con el texto del servidor tal cual. */}
              {errorDelServidor !== null && (
                <p
                  role="alert"
                  className="mb-3 rounded-lg border border-danger bg-danger-soft px-3 py-2.5 text-sm text-fg"
                >
                  <span className="font-medium">No se anotó la venta. </span>
                  {errorDelServidor}
                </p>
              )}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-fg-faint">
                    {cuantosMarcados === 0
                      ? 'Nada marcado'
                      : `${cuantosMarcados} producto(s) marcado(s)`}
                  </p>
                  <p className="text-xl font-semibold tabular-nums">
                    {formatCurrency(deCentavos(totalCentavos), { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={onClose}>
                    Cancelar
                  </Button>
                  {/* El contador de al lado ya alcanzaba —«Nada marcado»— pero
                      por casualidad y no por diseño: lo dice el botón, que es
                      donde se busca cuando no responde. */}
                  {/* El orden de los dos motivos no es casual: primero el que
                      la persona no puede levantar sola desde acá —está parada en
                      una empresa que no vende— y recién después el que sí
                      —todavía no marcó nada—. Al revés le pediría marcar
                      productos para una venta que igual no va a poder anotar. */}
                  {empresaNoVende ? (
                    <BotonBloqueado
                      variant="primary"
                      label="Anotar la venta"
                      motivo={venta.motivo}
                      salida={salidaDeEmpresa}
                    />
                  ) : cuantosMarcados === 0 && !confirmar.isPending ? (
                    <BotonBloqueado
                      variant="primary"
                      label="Anotar la venta"
                      motivo="Todavía no marcaste ningún producto. Tildá arriba lo que le vendiste y el total se arma solo."
                    />
                  ) : (
                    <Button
                      variant="primary"
                      loading={confirmar.isPending}
                      onClick={() => {
                        // El motivo anterior no sobrevive al intento siguiente.
                        setErrorDelServidor(null);
                        confirmar.mutate();
                      }}
                    >
                      Anotar la venta
                    </Button>
                  )}
                </div>
              </div>
              <p className="mt-3 rounded-lg border border-dashed border-line px-3 py-2 text-xs text-fg-muted">
                Esto <strong>no emite ninguna factura</strong>. Queda anotado qué se le vendió, por
                cuánto y cuándo vence, y suma a «vendido y sin facturar». Emitir el comprobante es
                un botón aparte, desde la grilla de abajo.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Lo que se ve después de confirmar: qué quedó anotado y los dos totales. */
function ResumenDeLaVenta({
  resultado,
  onClose,
  onOtra,
}: {
  resultado: ResultadoVentaRapida;
  onClose: () => void;
  onOtra: () => void;
}) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-success-soft text-success">
          <IconCheck className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <p className="font-semibold">
            Quedaron anotados {resultado.created} producto(s) por{' '}
            {formatCurrency(resultado.total, { minimumFractionDigits: 2 })}
          </p>
          <p className="mt-1 text-sm text-fg-muted">
            No se emitió ningún comprobante ({resultado.invoices_created} facturas creadas). Cuando
            haya que facturarlo, se abre la fila en la solapa «Ventas» del contacto y se usa «Armar
            factura en borrador»; confirmar el comprobante sigue siendo un clic aparte.
          </p>
        </div>
      </div>

      <h3 className="mt-5 text-sm font-semibold uppercase tracking-wide text-fg-faint">
        Lo que debe {resultado.totals.partner_name}
      </h3>
      <div className="mt-2">
        <TotalesDelClientePanel totales={resultado.totals} />
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onOtra}>
          Vender otra cosa
        </Button>
        <Button variant="primary" onClick={onClose}>
          Listo
        </Button>
      </div>
    </div>
  );
}

/** Spinner reexportado para que quien monte el diálogo no importe dos módulos. */
export { IconSpinner };
